import { Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Input,
  DestroyRef,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { BreakpointHelpers } from '../../../models/BreakpointHelpers';
import { CompanyYear } from '../../../models/CompanyYear';
import { Comparers } from '../../../models/Comparers';
import { BusinessFeatureEnum } from '../../../models/Enums/BusinessFeatureEnum';
import { SideNavRoute } from '../../../models/SideNavRoute';
import { User } from '../../../models/User';
import { AuthenticationService } from '../../../services/authentication.service';
import { BreakpointService } from '../../../services/breakpoint.service';
import { CompanyYearBusinessFeatureRangeService } from '../../../services/company-year-business-feature-range.service';
import { CompanyYearService } from '../../../services/company-year.service';
import { CoreModule } from '../../core.module';
import { PermissionService } from 'src/app/services/permission/permission.service';
import { ProductService } from 'src/app/services/product.service';
import { ConditionerService } from 'src/app/services/conditioner.service';
import { LocalStorageService } from 'src/app/services/storage/local-storage.service';
import { CommonService } from 'src/app/services/common.service';
import { Division } from 'src/app/models/features/divisions/Division';
import { DsmRsmDetails } from 'src/app/models/features/dsm-rsm/DsmRsm';
import { TraitProductConditionerService } from 'src/app/services/trait-product-conditioner.service';

@Component({
  selector: 'stine-navigation-bar',
  templateUrl: './navigation-bar.component.html',
  styleUrls: ['./navigation-bar.component.scss'],
  standalone: true,
  imports: [CoreModule, RouterModule, CommonModule, ReactiveFormsModule],
})
export class NavigationBarComponent implements OnInit, OnDestroy {
  @ViewChild('menuTrigger') public menuTrigger!: MatMenuTrigger;
  @ViewChild('dummyElement')
  public dummyElement!: ElementRef<HTMLInputElement>;

  @Output() public toggleSidenav = new EventEmitter<void>();
  @Output() public logoff = new EventEmitter<void>();
  @Output() public toggleStineMenu = new EventEmitter<void>();
  @Input() public topNavRoutes: SideNavRoute[] = [];

  public subscriptions: Subscription[] = [];
  public headerColor!: string;
  public name!: string;
  public currentUser?: User;
  private currentScreenSize!: string;
  public allRoutes: SideNavRoute[] = [];
  public filteredOptions: SideNavRoute[] = [];
  public currentDivision: Division = null;
  public currentIsr: DsmRsmDetails = null;

  // variables for CY
  private companyYears: CompanyYear[];
  public companyYearFormControl: UntypedFormControl;
  public selectedCompanyYear: CompanyYear | undefined;
  public yearComparer = Comparers.defaultComparer;
  public currentCY!: CompanyYear;
  public isCompanyYearLoading = true;

  public get smallScreenSize(): boolean {
    return (
      this.currentScreenSize === BreakpointHelpers.displayNameMap.get(Breakpoints.Small) ||
      this.currentScreenSize === BreakpointHelpers.displayNameMap.get(Breakpoints.XSmall)
    );
  }

  constructor(
    public rangeService: CompanyYearBusinessFeatureRangeService,
    private authService: AuthenticationService,
    private router: Router,
    private breakpointService: BreakpointService,
    public companyYearService: CompanyYearService,
    private destroyRef: DestroyRef,
    private permissionService: PermissionService,
    private productService: ProductService,
    private conditionerService: ConditionerService,
    private localStorageService: LocalStorageService,
    private commonService: CommonService,
    private traitProductConditionerService: TraitProductConditionerService,
  ) {
    this.companyYears = [];
    this.companyYearFormControl = new UntypedFormControl();
    this.selectedCompanyYear = undefined;
  }

  ngOnInit(): void {
    this.headerColor = environment.headerColor;
    this.name = environment.name.toUpperCase();
    this.subscriptions.push(
      this.authService.currentAuth$.subscribe((value) => {
        if (value != null) {
          this.currentUser = value.user;
          if (this.permissionService.isDivisionManager() && this.currentUser?.divisionId) {
            this.commonService
              .getUserDivisionById(this.currentUser?.divisionId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((response) => {
                this.currentDivision = response;
              });
          }
        }
      }),
      this.breakpointService.screenSize$.subscribe((value) => (this.currentScreenSize = value)),
    );

    this.companyYearService
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companyYearResponse) => {
        this.companyYears = companyYearResponse?.body?.items;
        this.rangeService
          .getCurrentByBusinessFeatureName(BusinessFeatureEnum.PRODUCTION)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((rangeResponse) => {
            this.currentCY = rangeResponse.companyYear;
            this.updateCurrentYear();
            this.getTraitProductAndConditionerList(true);
            this.isCompanyYearLoading = false;
          });
      });

    this.companyYearFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companyYear: CompanyYear) => {
        // set CY year globally
        this.changeCompanyYear(companyYear);
        this.companyYearFormControl.setValue(this.selectedCompanyYear, {
          emitEvent: false,
        });
        this.getTraitProductAndConditionerList(true);
      });

    this.productService.reloadProducts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reload) => {
        this.getTraitProductAndConditionerList(true);
      });
  }

  private getTraitProductAndConditionerList(forceReload: boolean = false): void {
    this.traitProductConditionerService.loadAll(this.selectedCompanyYear.year, '', 0, forceReload)
    .pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public get avatarText(): string {
    // if (this.permissionService.isRsa() || this.permissionService.isIsr()) {
    //   return `${this.currentUser.rep_SalesPerson}`;
    // }
    return `${this.currentUser?.person?.firstName?.[0] || ''}${this.currentUser?.person?.lastName?.[0] || ''}`;
  }

  public get roleText(): string {
    if (this.permissionService.isRsa()) {
      return `RSA (${this.currentUser.rep_SalesPerson})`;
    } else if (this.permissionService.isIsr()) {
      return `ISR (${this.currentUser.rep_SalesPerson}${this.currentIsr?.reP_TERRITORY ? ' - ' + this.currentIsr?.reP_TERRITORY : ''})`;
    } else if (this.permissionService.isSalesAdmin()) {
      return 'Sales Admin';
    } else if (this.permissionService.isDivisionManager()) {
      return `Division Manager (${this.currentDivision?.divisionCode ? this.currentDivision?.divisionCode : ''})`;
    } else if (this.permissionService.isSalesSupport()) {
      return `Sales Support`;
    }
    return '';
  }

  public updateCurrentYear(): void {
    this.selectedCompanyYear = this.companyYears.find((x) => x.year === this.currentCY.year);
    if (this.permissionService.isIsr()) {
      if (this.currentUser?.rep_SalesPerson && this.selectedCompanyYear?.year) {
        this.getRsaByIsrCode(this.selectedCompanyYear);
      }
    }
    this.companyYearService.setCurrentYear(this.selectedCompanyYear || new CompanyYear());
    this.companyYearService.updateCompanyYear(this.selectedCompanyYear || new CompanyYear());
    this.companyYearFormControl.setValue(this.selectedCompanyYear, {
      emitEvent: false,
    });
  }

  public isDisabled(child: any): boolean {
    return child.title == 'Report B' || child.title == 'Report c' || child.title == 'Report D';
  }

  public onProfile(): void {
    // TODO: need to add functionality on click on setting button
  }

  public onLogEvents(): void {
    this.router.navigate(['logevents']);
  }

  private changeCompanyYear(companyYear: CompanyYear): void {
    this.selectedCompanyYear = companyYear;
    this.companyYearService.updateCompanyYear(companyYear);
    if (this.permissionService.isIsr()) {
      if (this.currentUser?.rep_SalesPerson) {
        this.getRsaByIsrCode(companyYear);
      }
    }
  }

  private getRsaByIsrCode(companyYear: CompanyYear): void {
    this.commonService
      .getRsaByIsrCode(companyYear.year, this.currentUser?.rep_SalesPerson)
      .subscribe((response) => {
        this.currentIsr = response;
      });
  }

  public logout(): void {
    this.authService.logout();
    this.currentUser = undefined;
    this.menuTrigger?.closeMenu();
    this.router.navigate(['login']).then();
    this.logoff.emit();
  }

  //   public onChangePassword(): void {
  //     this.router.navigate(['user-confirmation'], {
  //       queryParams: { page: 'Change' },
  //     });
  //   }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public toggleSubMenu(route: any): void {
    route.isActive = !route.isActive;
  }

  public stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
